import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { BaseModal } from '../../components/common/UI/BaseModal';
import { useAccount } from '../../contexts/AccountContext';
import type { HelpTicket as Ticket, TicketResponse } from '../../services/helpService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { ResponseFormData } from './types';
import { formatTimestamp, getPriorityStyle, getStatusStyle } from './utils';

const EMPTY_ARRAY: never[] = [];

interface TicketDialogProps {
  open: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  responseForm: ResponseFormData;
  setResponseForm: React.Dispatch<React.SetStateAction<ResponseFormData>>;
  responseLoading: boolean;
  onAddResponse: () => void;
}

export const TicketDialog: React.FC<TicketDialogProps> = ({
  open,
  onClose,
  ticket,
  responseForm,
  setResponseForm,
  responseLoading,
  onAddResponse,
}) => {
  const theme = useTheme();
  const { account: user, hasPermission } = useAccount();

  const handleResponseFormChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setResponseForm({ ...responseForm, message: e.target.value });
  };

  if (!ticket) {
    return null;
  }

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={
        <Box>
          <Typography variant="h5">
            REF: #{ticket.ticketNumber} [{ticket.category.toUpperCase()}] - {ticket.title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              label={
                ticket.lastResponseAuthorType === 'support'
                  ? 'Awaiting User Response'
                  : 'Awaiting Support Response'
              }
              size="small"
              sx={
                (ticket.lastResponseAuthorType === 'support' &&
                  !hasPermission(PERMISSIONS.TICKET_MANAGE)) ||
                (ticket.lastResponseAuthorType === 'user' &&
                  hasPermission(PERMISSIONS.TICKET_MANAGE))
                  ? {
                      backgroundColor: theme.palette.bidlogiq.rust,
                      color: theme.palette.common.white,
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                    }
                  : {
                      backgroundColor: theme.palette.greyLight,
                      color: theme.palette.text.primary,
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                    }
              }
            />
            <Chip label={ticket.status} size="small" sx={getStatusStyle(ticket.status, theme)} />
            <Chip
              label={ticket.priority}
              size="small"
              sx={getPriorityStyle(ticket.priority, theme)}
            />
          </Box>
        </Box>
      }
      actions={
        <>
          <Button
            variant="contained"
            onClick={onAddResponse}
            sx={{
              backgroundColor: theme.palette.bidlogiq.brick,
              color: theme.palette.bidlogiq.white,
              fontSize: '1.1rem',
              fontWeight: 500,
              '&:hover': { backgroundColor: theme.palette.bidlogiq.rust },
            }}
            disabled={!responseForm.message.trim() || responseLoading}
          >
            {responseLoading ? <CircularProgress size={24} /> : 'Add Response'}
          </Button>
          <Button
            onClick={onClose}
            sx={{
              backgroundColor: theme.palette.bidlogiq.rust,
              color: theme.palette.bidlogiq.white,
              fontSize: '1.1rem',
              fontWeight: 500,
            }}
          >
            Close
          </Button>
        </>
      }
    >
      <Box>
        <Typography variant="body1">
          {user?.displayName && user?.displayName !== 'N/A' ? user?.displayName : user?.email}
        </Typography>
        <Typography variant="body1">Created: {formatTimestamp(ticket.createdAt)}</Typography>
        <Typography variant="body1">Last updated: {formatTimestamp(ticket.updatedAt)}</Typography>
        {Array.isArray(ticket.responses) && ticket.responses.length > 0 ? (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(ticket.responses || EMPTY_ARRAY).map((response: TicketResponse, index: number) => {
              const getBorderColor = () => {
                if (response.isSystemMessage) {
                  return theme.palette.grey[400];
                }
                if (response.authorType === 'client_admin' || response.authorType === 'support') {
                  return theme.palette.bidlogiq.brick;
                }
                return theme.palette.primary.main;
              };

              return (
                <Card
                  key={response.id || index}
                  variant="outlined"
                  sx={{
                    borderLeft: `4px solid ${getBorderColor()}`,
                    ml: response.authorType === 'user' ? 0 : 4,
                    mr: response.authorType === 'user' ? 4 : 0,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Box
                      sx={{
                        width: '100%',
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="subtitle1"
                          color="text.primary"
                          fontSize="1.1rem"
                          fontWeight={500}
                        >
                          {response.authorName && response.authorName !== 'N/A'
                            ? response.authorName
                            : response.authorType === 'client_admin' ||
                                response.authorType === 'support'
                              ? 'Support Team'
                              : 'User'}
                        </Typography>
                        <Chip
                          label={
                            response.authorType === 'client_admin' ||
                            response.authorType === 'support'
                              ? 'Support'
                              : 'User'
                          }
                          size="small"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                        {response.isSystemMessage && (
                          <Chip
                            label="System"
                            size="small"
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="subtitle2" fontSize="0.85rem" color="text.secondary">
                        {formatTimestamp(response.timestamp as Date)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.primary"
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {response.message || response.response}
                    </Typography>
                    {response.attachments && response.attachments.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                        {response.attachments.map((attachment) => (
                          <Link
                            key={attachment.filename}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'block',
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              overflow: 'hidden',
                              boxShadow: 1,
                              '&:hover': { opacity: 0.8 },
                            }}
                          >
                            <img
                              src={attachment.url}
                              alt={attachment.filename}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </Link>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Box sx={{ width: '100%', mb: 1 }}>
              <Typography variant="subtitle1" color="text.primary">
                {user?.displayName && user?.displayName !== 'N/A' ? user?.displayName : user?.email}
              </Typography>
              <Typography variant="caption">{formatTimestamp(ticket.createdAt)}</Typography>
            </Box>
            <Typography variant="body1" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </Typography>
            {ticket.attachments && ticket.attachments.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                {ticket.attachments.map((attachment) => (
                  <Link
                    key={attachment.filename}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: 'hidden',
                      boxShadow: 1,
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    <img
                      src={attachment.url}
                      alt={attachment.filename}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Link>
                ))}
              </Box>
            )}
          </Paper>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add Response
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Type your response here..."
            value={responseForm.message}
            onChange={handleResponseFormChange}
            slotProps={{
              htmlInput: { maxLength: 5000 },
            }}
            helperText={`${responseForm.message.length}/5000`}
          />
        </Box>
      </Box>
    </BaseModal>
  );
};
